import React, { Fragment, useEffect } from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { isPast } from 'date-fns';

import { Image } from '../../common/Image';
import { BaseText } from '../../common/BaseText';
import { Touchable } from '../../common/Touchable';
import { Separator } from '../../common/Separator';
import { AvatarImage } from '../../common/AvatarImage';
import { IconTouchable } from '../../common/IconTouchable';
import {
  MainStackParamListType,
  TabParamListType,
  screens,
  ScreensType,
  ScreensNameType,
} from '@navigation';
// import { LanguageMenu } from '../LanguageMenu';
import { NotificationMenu } from './NotificationMenu';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useNotificationsModel } from '@models/Notifications/NotificationsModel';
// import { LanguageMenu } from '../LanguageMenu';
import { useChatsModel } from '@models/Chats/ChatsModel';
import { WebHeaderButton } from './WebHeaderButton';
import config from '@config';

const Container = styled(View)({
  backgroundColor: '$background0',
  height: 78,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',
  px: ['$6', '$7', '5%'],
});

const LogoImage = styled(Image)({
  width: 130,
  height: 38,
});

const BadgeContainer = styled(View)({
  size: 24,
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: 12,
  bg: '$primary300',
});

const BadgePlaceholder = styled(View)({
  size: 24,
});

const BadgeText = styled(BaseText)({
  variant: 'text.xsmall',
  color: '$background0',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const ButtonsTextContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  ml: '18%',
});

const FullNameText = styled(BaseText)({
  color: '$heading600',
  variant: 'text.small',
  ml: '$4',
});

function iconName(
  screenName: ScreensNameType,
  isActive: boolean,
): string {
  switch (screenName) {
    case screens.Hub:
      return isActive ? 'education-bold' : 'education-light';
    case screens.Chats:
      return isActive ? 'message-bold' : 'message-light';
    case screens.TrackFeedback:
      return isActive ? 'music-bold' : 'music-light';
    default:
      return '';
  }
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

interface IWebHeaderProps {
  isShowTabButtons?: boolean;
  activeTabButtonName?: keyof TabParamListType | ScreensType['Chats'];
}

export const WebHeader = observer<IWebHeaderProps>(
  ({ isShowTabButtons = true, activeTabButtonName }) => {
    const nav = useNavigation<NavigationPropType>();
    const notifications = useNotificationsModel();
    const chats = useChatsModel();
    const viewer = useViewerModel();
    const { t } = useTranslation();

    useEffect(() => {
      if (!isShowTabButtons) {
        return;
      }

      fetchNotificationsCount();
      fetchChatsCount();
    }, []);

    async function fetchNotificationsCount() {
      if (notifications.list.hasReadingInProgress) {
        return;
      }

      try {
        await notifications.fetchCount.run();
      } catch (error) {
        // Do nothing
      }
    }

    async function fetchChatsCount() {
      try {
        await chats.fetchCount.run();
      } catch (error) {
        // Do nothing
      }
    }

    function navigateToHome() {
      nav.navigate(screens.TabNavigator, {
        screen: screens.HomeTabNavigator,
      });
    }

    function navigateToHub() {
      nav.navigate(screens.TabNavigator, { screen: screens.Hub });
    }

    function navigateToChats() {
      nav.navigate(screens.ChatNavigator, { screen: screens.Chats });
    }

    function navigateToProfile() {
      nav.navigate(screens.TabNavigator, { screen: screens.Profile });
    }

    function navigateToTrackFedback() {
      nav.navigate(screens.TabNavigator, {
        screen: screens.TrackFeedback,
      });
    }

    function navigateToContactUsForm() {
      nav.navigate(screens.ProfileNavigator, {
        screen: screens.ContactUs,
      });
    }

    // const isActiveHome = activeTabButtonName === screens.Home;
    const isActiveHub = activeTabButtonName === screens.Hub;
    const isActiveChat = activeTabButtonName === screens.Chats;
    const isActiveTrackFeedback =
      activeTabButtonName === screens.TrackFeedback;
    // const isActiveProfile = activeTabButtonName === screens.Profile;

    const isShowTrackFeedback =
      isPast(new Date(2023, 11, 31, 23, 59, 59)) ||
      config.ENV !== 'production';

    return (
      <Container>
        <RowContainer>
          <Touchable
            onPress={navigateToHome}
            disabled={!isShowTabButtons}
          >
            <LogoImage
              source={require('../../../../assets/images/logo.png')}
            />
          </Touchable>
          {isShowTabButtons && (
            <ButtonsTextContainer>
              <WebHeaderButton
                onPress={navigateToHub}
                isActive={isActiveHub}
                iconName={iconName(screens.Hub, isActiveHub)}
              >
                {t('common.webHeader.hub')}
              </WebHeaderButton>

              {isShowTrackFeedback && (
                <RowContainer>
                  <Separator size="$8" vertical />
                  <WebHeaderButton
                    onPress={navigateToTrackFedback}
                    isActive={isActiveTrackFeedback}
                    iconName={iconName(
                      screens.TrackFeedback,
                      isActiveTrackFeedback,
                    )}
                  >
                    {t('common.webHeader.trackFeedback')}
                  </WebHeaderButton>
                </RowContainer>
              )}

              <Separator size="$8" vertical />
              <WebHeaderButton
                onPress={navigateToChats}
                isActive={isActiveChat}
                iconName={iconName(screens.Chats, isActiveChat)}
              >
                {t('common.webHeader.chat')}
              </WebHeaderButton>
              <Separator size="$5" vertical />

              {chats.hasNewMessages ? (
                <BadgeContainer>
                  <BadgeText>{chats.formattedNewCount}</BadgeText>
                </BadgeContainer>
              ) : (
                <BadgePlaceholder />
              )}
            </ButtonsTextContainer>
          )}
        </RowContainer>

        <RowContainer>
          {/* <LanguageMenu /> */}
          {isShowTabButtons && (
            <Fragment>
              <Separator size="$7" vertical />
              <IconTouchable
                onPress={navigateToContactUsForm}
                name="question-square"
              />
              <Separator size="$7" vertical />
              <NotificationMenu />
              <Separator size="$7" vertical />

              <Touchable onPress={navigateToProfile}>
                <RowContainer>
                  <AvatarImage
                    size={28}
                    source={{
                      uri: viewer.currentUser?.photo.small?.url,
                    }}
                  />
                  <FullNameText>
                    {viewer.currentUser?.truncatedFullName}
                  </FullNameText>
                </RowContainer>
              </Touchable>
            </Fragment>
          )}
        </RowContainer>
      </Container>
    );
  },
);
